import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";
import { transformHtmlUrls } from "@/lib/storageUrl";

interface HtmlContentProps {
  content: string;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Safely renders HTML content with DOMPurify sanitization.
 * Use this component to display rich text content from the RichTextEditor.
 */
export function HtmlContent({ 
  content, 
  className,
  as: Component = "div" 
}: HtmlContentProps) {
  // Return null or empty if no content
  if (!content) return null;
  
  // If content doesn't contain HTML tags, render as plain text
  const hasHtml = /<[^>]+>/.test(content);
  
  if (!hasHtml) {
    return <Component className={className}>{content}</Component>;
  }
  
  // Transform Supabase storage URLs to clean domain URLs
  const transformedContent = transformHtmlUrls(content);
  
  // Sanitize and render HTML
  const sanitizedHtml = DOMPurify.sanitize(transformedContent, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'img', 'span', 'div', 'mark', 'hr'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'src', 'alt', 'title', 
      'class', 'style', 'width', 'height'
    ],
  });

  return (
    <Component
      className={cn("prose prose-sm max-w-none dark:prose-invert", className)}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}